import { Logger } from "../../utils/Logger";
import { DataSyncLibEventNames } from "../DataSyncLibEventNames";
import { inject, injectable, singleton } from "tsyringe";
import { ServiceTokens } from "../ServiceTokens";
import { Coordinates, GeoCoordinatesService } from "./GeoCoordinatesService";
import { isValidPostalCode, normalizePostalCode } from "../../utils/PostalCode";

declare const GeoIpUrl: string; // Set in the webpack config file

@injectable()
@singleton()
export class GeoIpService {
  private requestStarted = false;
  private cacheKey = "geoCache";
  private readonly dataLayerKey = "user.detectedPostalCode";
  private readonly notAvailable = "NA";

  constructor(@inject(ServiceTokens.GeoCoordinatesService) private geoCoordinatesService: GeoCoordinatesService) {}

  /**
   * Fetches the postal code of the user based on the geolocation data.
   * @returns The postal code of the user if it was successfully fetched, otherwise undefined.
   * @remarks To prevent multiple calls to the GeoIp service, the result is stored in the dataLayer.
   * If the postal code is already stored in the dataLayer, it will be returned.
   * If the result of the GeoIp service is not a valid postal code, the result will be "NA".
   */
  public async fetchAndStoreUserPostalCode(): Promise<string | undefined> {
    if (navigator.locks === undefined) {
      Logger.warn("Locks API not supported. Fetching postal code without locking.");
      return this.doFetchAndStoreUserPostalCode();
    }
    let postalCode;
    await navigator.locks.request("geoIpRequest", async () => {
      postalCode = await this.doFetchAndStoreUserPostalCode();
    });
    return postalCode;
  }

  private async doFetchAndStoreUserPostalCode(): Promise<string | undefined> {
    let result = undefined;
    const storedPostalCode = dataLayerHelper.get<string>(this.dataLayerKey); // Used in GTM for Lotame!
    if (storedPostalCode === this.notAvailable) {
      return undefined;
    }
    if (storedPostalCode) {
      Logger.verbose("Got postal code from dataLayer", storedPostalCode);
      return storedPostalCode;
    }

    const coordinates = this.geoCoordinatesService.getCoordinates();
    if (!coordinates) {
      Logger.verbose("Coordinates are not available");
      return undefined;
    }

    if (!this.isWithinCanadaBoundaries(coordinates)) {
      Logger.verbose("Coordinates are not inside Canada extreme boundaries");
      return undefined;
    }

    const cache = this.tryGetCoordinatesFromCache();
    if (cache && cache.lat === coordinates.latitude && cache.long === coordinates.longitude) {
      if (cache.postalCode === this.notAvailable) {
        return undefined;
      }
      Logger.verbose("Got postal code from cache", cache.postalCode); // Used in tests!
      if (isValidPostalCode(cache.postalCode)) {
        this.sendGeoLocationCompleted(cache.postalCode);
        return cache.postalCode;
      }
    }

    // Should not be required if navigator.locks is available.
    if (this.requestStarted) {
      Logger.verbose("GeoIp request already started");
      return this.createResolutionPromise();
    }

    this.requestStarted = true;
    Logger.verbose("Fetching geoip data", coordinates);

    const serviceUri = `${GeoIpUrl}?lat=${coordinates.latitude}&lon=${coordinates.longitude}`;
    try {
      const response = await fetch(serviceUri);
      if (!response.ok) {
        Logger.log(`Http error while fetching Geoip data. Status: ${response.status}`);
      } else {
        const data = await response.json();
        if (typeof data === "string" && isValidPostalCode(data.trim())) {
          Logger.verbose("Fetch complete. Got", data);
          result = normalizePostalCode(data);
          this.updateCache(coordinates.latitude, coordinates.longitude, result);
        } else {
          this.updateCache(coordinates.latitude, coordinates.longitude, this.notAvailable);
          const dataString = typeof data === "object" ? JSON.stringify(data) : String(data);
          Logger.warn(`Received data is not a valid postal code. Received data: ${dataString}`);
        }
      }
    } catch (error) {
      Logger.error(`Failed to fetch geoip data. Error: ${error}, Uri: ${serviceUri}`);
    }

    this.sendGeoLocationCompleted(result ?? this.notAvailable);
    Logger.verbose("GeoIp request completed", result); // Used in tests!
    return result;
  }

  private sendGeoLocationCompleted(result: string): void {
    dataLayerQueue.push({ user: { detectedPostalCode: result } });
    window.dispatchEvent(new Event(DataSyncLibEventNames.GeolocationDataLoaded));
  }

  /**
   * Returns a promise that will resolve once the postal code resolution has completed.
   * @private
   */
  private createResolutionPromise(): Promise<string | undefined> {
    return new Promise((resolve) => {
      window.addEventListener(DataSyncLibEventNames.GeolocationDataLoaded, () => {
        const dataLayerPostalCode = dataLayerHelper.get<string>(this.dataLayerKey);
        if (dataLayerPostalCode !== this.notAvailable) {
          resolve(dataLayerPostalCode);
        } else {
          resolve(undefined);
        }
      });
    });
  }

  /**
   * Checks if the given coordinates are within the bounds of Canada.
   * @remarks This function is not precise. And could inclue some areas outside of Canada. (France (Saint-Pierre de Miquelon), Greenland and United States of America)
   * @param coordinates
   * @private
   */
  private isWithinCanadaBoundaries(coordinates: Coordinates): boolean {
    const CANADA_EXTREME_POINTS = {
      MIN_LATITUDE: 41.41824, // Middle Island, Ontario
      MAX_LATITUDE: 83.082613, // Cap Columbia, Nunavut
      MIN_LONGITUDE: -141.0, // Yukon / Alaska border
      MAX_LONGITUDE: -52.620079, // Cap Spear, Newfoundland and Labrador
    } as const;

    const latitude = Number(coordinates.latitude);
    const longitude = Number(coordinates.longitude);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      Logger.warn(`Invalid latitude or longitude values, not a number: latitude=${latitude}, longitude=${longitude}`);
      return false;
    }

    return (
      latitude >= CANADA_EXTREME_POINTS.MIN_LATITUDE &&
      latitude <= CANADA_EXTREME_POINTS.MAX_LATITUDE &&
      longitude >= CANADA_EXTREME_POINTS.MIN_LONGITUDE &&
      longitude <= CANADA_EXTREME_POINTS.MAX_LONGITUDE
    );
  }

  private tryGetCoordinatesFromCache(): CoordinatesCache | undefined {
    const cache = localStorage.getItem("geoCache");
    let coordinatesCache: CoordinatesCache;
    if (cache) {
      try {
        coordinatesCache = JSON.parse(cache) as CoordinatesCache;
      } catch (error) {
        Logger.error(`Failed to parse cache. Error: ${error}`);
        return undefined;
      }

      if (!coordinatesCache.lat || !coordinatesCache.long || !coordinatesCache.postalCode) {
        Logger.warn(`Invalid cache. Cache: ${cache}`);
        return undefined;
      }
      return coordinatesCache;
    }
  }

  private updateCache(latitude: string, longitude: string, result: string): void {
    const cache: CoordinatesCache = {
      lat: latitude,
      long: longitude,
      postalCode: result,
    };
    localStorage.setItem(this.cacheKey, JSON.stringify(cache));
    Logger.verbose("GeoIp cache updated", cache); // Used in tests!
  }
}

interface CoordinatesCache {
  lat: string;
  long: string;
  postalCode: string;
}
